import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Chip, Grid, Stack, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { toSportSlug } from '@utils/sports';
import { useSportModules } from '@hooks/useSportModules';
import {
  countPublicModules,
  getSportReadinessSummary,
  STATE_COLORS,
  STATE_LABELS,
} from '@utils/sportModules';
import { SPORT_DISCOVERY_CARDS } from './sportDiscovery';

const SportDiscoveryGrid: React.FC = () => {
  const navigate = useNavigate();
  const { data: moduleMatrix } = useSportModules();

  return (
    <Box sx={{ mt: 6 }}>
      <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.5 }}>
        Choose Your Sport
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 680 }}>
        Open a sport hub for rubric, learning, rankings and contests — rollout status reflects live module matrix.
      </Typography>

      <Grid container spacing={2}>
        {SPORT_DISCOVERY_CARDS.map((card) => {
          const readiness = getSportReadinessSummary(moduleMatrix, card.sport);
          const publicModules = countPublicModules(moduleMatrix, card.sport);
          const isLive = card.skills !== '—' && readiness.state === 'public';
          return (
            <Grid item xs={12} sm={6} md={4} key={card.sport}>
              <Box
                sx={{
                  height: '100%',
                  p: 2.5,
                  borderRadius: 3,
                  background: card.gradient,
                  border: card.highlight ? `2px solid ${card.accent}` : '1px solid #e5e7eb',
                  boxShadow: card.highlight ? `0 8px 28px ${card.accent}22` : '0 4px 16px rgba(15,23,42,0.06)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 14px 32px ${card.accent}28`,
                  },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: '#fff',
                      color: card.accent,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    }}
                  >
                    {card.icon}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                      {card.sport}
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                      {card.highlight && (
                        <Chip
                          label="Most complete"
                          size="small"
                          sx={{ height: 20, fontSize: '0.65rem', fontWeight: 800, bgcolor: card.accent, color: '#fff' }}
                        />
                      )}
                      <Chip
                        label={STATE_LABELS[readiness.state]}
                        size="small"
                        color={STATE_COLORS[readiness.state]}
                        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}
                      />
                    </Stack>
                  </Box>
                </Stack>

                {isLive ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                    <Chip
                      label={`${card.skillLabel}: ${card.skills}`}
                      size="small"
                      sx={{ fontWeight: 700, bgcolor: 'rgba(255,255,255,0.85)', color: card.accent }}
                    />
                    <Chip label={`Drills: ${card.drills}`} size="small" sx={{ fontWeight: 700, bgcolor: 'rgba(255,255,255,0.85)' }} />
                    <Chip label={`Fitness: ${card.fitness}`} size="small" sx={{ fontWeight: 700, bgcolor: 'rgba(255,255,255,0.85)' }} />
                    {publicModules > 0 && (
                      <Chip
                        label={`${publicModules}/4 public tabs`}
                        size="small"
                        sx={{ fontWeight: 700, bgcolor: 'rgba(255,255,255,0.85)' }}
                      />
                    )}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {readiness.label} — explore structure on the sport hub.
                  </Typography>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  endIcon={<OpenInNewIcon />}
                  onClick={() => navigate(`/sport/${toSportSlug(card.sport)}`)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                    bgcolor: card.accent,
                    boxShadow: `0 6px 16px ${card.accent}44`,
                    '&:hover': { bgcolor: card.accent, filter: 'brightness(1.08)' },
                  }}
                >
                  Open {card.sport} hub
                </Button>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default SportDiscoveryGrid;
